import { useLayoutEffect, useState } from "react";
import { RouterProvider, type createHashRouter } from "react-router-dom";

type Router = ReturnType<typeof createHashRouter>;

/**
 * `RouterProvider`, mounted again if the router moved on before it subscribed. It reads
 * `router.state` as it renders but subscribes only once it commits, and under `LocaleGate` it
 * renders in a Suspense retry, which React may pause partway. The first route's `lazy` resolving in
 * that gap leaves it on the loading mojo for good.
 *
 * Pass the same `router` for its whole life, as `root.tsx` does: the check is against the state the
 * first one rendered with.
 */
export function SubscribedRouterProvider({ router }: { router: Router }) {
  const [remountKey, setRemountKey] = useState(0);
  // Only the first mount can miss an update: once subscribed, RouterProvider hears every one.
  const [rendered] = useState(() => router.state);
  useLayoutEffect(() => {
    // RouterProvider has subscribed by now: a child's layout effects run before its parent's.
    if (router.state !== rendered) setRemountKey((key) => key + 1);
  }, [router, rendered]);
  return <RouterProvider key={remountKey} router={router} />;
}
